"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const moment = require("moment");
const _1 = require("../");
class TaskModel extends _1.BaseModel {
    // sorts activities by date or name. for a descending sort, set sortOrder to -1
    static sortActivities(tasks, sortBy = 'Date', sortOrder = 1) {
        if (sortBy === 'Date') {
            TaskModel.sortByDate(tasks, sortOrder);
        }
        else if (sortBy === 'Name') {
            TaskModel.sortByName(tasks, sortOrder);
        }
        else {
            throw new Error(`Unsupported sort specified: ${sortBy}`);
        }
    }
    static sort(tasks) {
        tasks.sort((task1, task2) => {
            // closed tasks at the bottom
            if (task1.isComplete && !task2.isComplete) {
                return 1;
            }
            else if (!task1.isComplete && task2.isComplete) {
                return -1;
            }
            else if (task1.isComplete && task2.isComplete) {
                return 0;
            }
            // open tasks with null due dates
            if ((task1.dueDate === null) && (task2.dueDate === null)) {
                return 0;
            }
            else if (task1.dueDate === null) {
                return 1;
            }
            else if (task2.dueDate === null) {
                return -1;
            }
            // open tasks ordered by due date, secondary by lastModifiedDate
            if (task1.dueDate > task2.dueDate) {
                return 1;
            }
            else if (task1.dueDate < task2.dueDate) {
                return -1;
            }
            else {
                if (task1.lastModifiedDate > task2.lastModifiedDate) {
                    return 1;
                }
                else if (task1.lastModifiedDate < task2.lastModifiedDate) {
                    return -1;
                }
                else {
                    return 0;
                }
            }
        });
    }
    static newSkippedActivity(contact) {
        const activity = TaskModel.newLoggedActivity(contact);
        activity.subject = TaskModel.RESULT.SKIPPED;
        activity.callResult = TaskModel.RESULT.SKIPPED;
        activity.type = TaskModel.TYPE.CALL;
        return activity;
    }
    static newLoggedCall(contact) {
        const loggedCall = new TaskModel();
        loggedCall.callResult = TaskModel.RESULT.REACHED;
        loggedCall.dueDate = new Date().toISOString();
        loggedCall.isComplete = true;
        loggedCall.type = TaskModel.TYPE.CALL;
        if (contact) {
            loggedCall.contact = contact;
            loggedCall.contactId = contact._id;
        }
        return loggedCall;
    }
    static newLoggedActivity(contact) {
        const loggedActivity = new TaskModel();
        loggedActivity.dueDate = new Date().toISOString();
        loggedActivity.isComplete = true;
        if (contact) {
            loggedActivity.contact = contact;
            loggedActivity.contactId = contact._id;
        }
        return loggedActivity;
    }
    // sorts tasks by date. for a descending sort, set sortOrder to -1
    static sortByDate(tasks, sortOrder = 1) {
        const getPrimarySortFieldValue = (task) => {
            return (task.isTask) ? task.lastModifiedDate : task.dueDate;
        };
        const getSecondarySortFieldValue = (task) => {
            return (task.isTask) ? task.dueDate : task.lastModifiedDate;
        };
        // for actual tasks order by last modified date with most recent first, secondary sort by due date, or
        // for other activities order by due date with most recent first, secondary sort by last modified date
        // for a descending sort, flip the logic above.
        tasks.sort((task1, task2) => {
            const task1PrimaryDate = getPrimarySortFieldValue(task1);
            const task2PrimaryDate = getPrimarySortFieldValue(task2);
            if (task1PrimaryDate > task2PrimaryDate) {
                return -sortOrder;
            }
            else if (task1PrimaryDate < task2PrimaryDate) {
                return sortOrder;
            }
            else {
                const task1SecondaryDate = getSecondarySortFieldValue(task1);
                const task2SecondaryDate = getSecondarySortFieldValue(task2);
                if (task1SecondaryDate > task2SecondaryDate) {
                    return -sortOrder;
                }
                else if (task1SecondaryDate < task2SecondaryDate) {
                    return sortOrder;
                }
            }
            return 0;
        });
    }
    // sorts tasks by name. for a descending sort, set sortOrder to -1
    static sortByName(tasks, sortOrder = 1) {
        const getPrimarySortFieldValue = (activity) => {
            return activity.isTask ? activity.subject : activity.description;
        };
        tasks.sort((task1, task2) => {
            const sortValue1 = getPrimarySortFieldValue(task1);
            const sortValue2 = getPrimarySortFieldValue(task2);
            if (sortValue1 > sortValue2) {
                return sortOrder;
            }
            else if (sortValue1 > sortValue2) {
                return -sortOrder;
            }
            else {
                return 0;
            }
        });
    }
    get salesforceFieldMap() {
        return {
            _id: 'Id',
            callDisposition: 'CallDisposition',
            callResult: 'McLabs2__Call_Result__c',
            contactId: 'WhoId',
            description: 'Description',
            dueDate: 'ActivityDate',
            lastModifiedDate: 'SystemModstamp',
            completedDate: 'CompletedDateTime',
            marketingStatus: 'McLabs2__Marketing_Phase__c',
            ownerId: 'OwnerId',
            priority: 'Priority',
            recordTypeId: 'RecordTypeId',
            status: 'Status',
            subject: 'Subject',
            type: 'Type',
            whatId: 'WhatId'
        };
    }
    // TODO remove "subject" someday
    get name() {
        return this.subject;
    }
    set name(name) {
        this.subject = name;
    }
    get isTask() {
        return !this.callDisposition && !this.callResult;
    }
    get isOverdue() {
        return !this.isComplete && this.dueDate && this._isOverdue;
    }
    get dueDate() {
        return this._dueDate;
    }
    set dueDate(value) {
        this._dueDate = value;
        this._isOverdue = moment().isAfter(moment(this._dueDate, 'YYYY-MM-DD'), 'day');
    }
    getDueDateString(format = 'YYYY-MM-DD') {
        if (!this.dueDate) {
            return null;
        }
        return moment(this.dueDate).format(format);
    }
    // set a default subject. main use case if for activities.
    setDefaultSubject(force) {
        if (this.subject && this.subject.trim() && !force) {
            return;
        }
        let subject = this.type;
        if (this.contact) {
            subject += ` with ${this.contact.fullName}`;
        }
        if (this.dueDate) {
            subject += ` on ${this.getDueDateString()}`;
        }
        this.subject = subject;
    }
    transformToSalesforce() {
        const sfTask = super.transformToSalesforce();
        delete sfTask.SystemModstamp;
        delete sfTask.CompletedDate;
        if (this.isComplete && this.status !== TaskModel.STATUS.COMPLETE) {
            sfTask.Status = TaskModel.STATUS.COMPLETE;
        }
        else if (!this.isComplete && this.status === TaskModel.STATUS.COMPLETE) {
            sfTask.Status = TaskModel.STATUS.IN_PROGRESS;
        }
        // legacy handling for mobile. if the activity date was passed to the server as a DateTime,
        // converted it to a Date
        if (sfTask.ActivityDate && sfTask.ActivityDate.indexOf('T') !== -1) {
            sfTask.ActivityDate = this.getDueDateString();
        }
        return sfTask;
    }
    toJSON() {
        const json = {};
        Object.keys(this).forEach(key => json[key] = this[key]);
        json.dueDate = this._dueDate;
        return json;
    }
    transformFromSalesforce(sfTask) {
        super.transformFromSalesforce(sfTask);
        this.whatName = sfTask.What && sfTask.What.Name;
        this.isComplete = (this.status === TaskModel.STATUS.COMPLETE);
        this.lastModifiedDate = sfTask.SystemModstamp
            || new Date().toISOString();
        if (sfTask.Owner) {
            this.ownerName = sfTask.Owner.Name;
        }
        // if there is no callResult but there is a callDisposition (possible for activities created directly
        // in Salesforce), then set the callResult to equal the callDisposition for consistent handling in our apps.
        if (!this.callResult && this.callDisposition) {
            this.callResult = this.callDisposition;
        }
    }
    transformFromMappingJson(json) {
        super.transformFromMappingJson(json);
        this.dueDate = json.dueDate;
    }
    transformFromJson(json) {
        super.transformFromJson(json);
        this.dueDate = json.dueDate;
    }
}
TaskModel.MARKETING_STATUSES = {
    CONTACTED: 'Contacted',
    MATERIALS_SENT: 'Materials Sent',
    TOURED: 'Toured',
    OFFERED: 'Offered'
};
TaskModel.RESULT = {
    INBOUND: 'Inbound',
    LEFT_MESSAGE: 'Left Message',
    NOT_REACHED: 'Not Reached',
    REACHED: 'Reached',
    SENT_EMAIL: 'Send Email',
    SKIPPED: 'Skipped',
    OTHER: 'Other'
};
TaskModel.TYPE = {
    CALL: 'Call',
    EMAIL: 'Email',
    MEETING: 'Meeting',
    TOUR: 'Tour',
    OTHER: 'Other'
};
TaskModel.PRIORITY = {
    HIGH: 'High',
    NORMAL: 'Normal',
    LOW: 'Low',
    CRITICAL_DATE: 'Critical date'
};
TaskModel.STATUS = {
    COMPLETE: 'Completed',
    IN_PROGRESS: 'In Progress'
};
exports.TaskModel = TaskModel;
//# sourceMappingURL=task.model.js.map